import React from 'react'
import { motion } from 'framer-motion'
import { usePortfolioStore } from '../../store/portfolioStore'
import { pageContainerVariants, pageItemVariants } from '../../constants/motionVariants'
import PortfolioChart from '../PortfolioChart'
import GoldPortfolioSummary from '../GoldPortfolioSummary'
import EmptyState from '../EmptyState'
import MarketIndices from '../MarketIndices'

const Sk = ({ className = '', style }: { className?: string; style?: React.CSSProperties }) => (
  <div className={`bg-gray-200 dark:bg-gray-700 rounded animate-pulse ${className}`} style={style} />
)

const PortfolioPageSkeleton: React.FC = () => (
  <div className="space-y-8">
    {/* MarketIndices skeleton */}
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
      <div className="flex flex-wrap gap-2">
        {[88, 72, 96, 78, 58, 62, 68].map((w, i) => (
          <div key={i} className="h-8 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" style={{ width: `${w}px` }} />
        ))}
      </div>
    </div>

    {/* PortfolioChart skeleton */}
    <div className="card p-6 ">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {["stat-tile-primary", "stat-tile-violet", "stat-tile-gain", "stat-tile-gain"].map((t, i) => (
          <div key={i} className={`stat-tile ${t}`}>
            <div className="h-3 w-20 bg-white/60 dark:bg-white/10 rounded animate-pulse" />
            <div className="h-7 w-32 bg-white/60 dark:bg-white/10 rounded-lg animate-pulse" />
            <div className="h-3 w-24 bg-white/60 dark:bg-white/10 rounded animate-pulse" />
          </div>
        ))}
      </div>

      {/* Chart title + switcher */}
      <div className="flex items-center justify-between mb-6">
        <Sk className="h-6 w-48" />
        <Sk className="h-8 w-44 rounded-lg" />
      </div>

      {/* Chart + legend */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
        {/* Pie chart placeholder */}
        <div className="h-80 lg:col-span-2 flex items-center justify-center">
          <Sk className="w-56 h-56 !rounded-full" />
        </div>

        {/* Legend table */}
        <div className="space-y-3 lg:col-span-8">
          {/* Header */}
          <div className="grid grid-cols-11 gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-600">
            <div className="col-span-3"><Sk className="h-3 w-12" /></div>
            <div className="col-span-2 flex justify-end"><Sk className="h-3 w-20" /></div>
            <div className="col-span-2 flex justify-end"><Sk className="h-3 w-20" /></div>
            <div className="col-span-2 flex justify-end"><Sk className="h-3 w-20" /></div>
            <div className="col-span-2 flex justify-end"><Sk className="h-3 w-16" /></div>
          </div>

          {/* Rows */}
          {[70, 55, 80, 65, 75].map((w, i) => (
            <div key={i} className="grid grid-cols-11 gap-2 items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
              <div className="col-span-3 flex items-center gap-3">
                <div className="w-0.5 h-4 rounded-sm bg-gray-200 dark:bg-gray-600" />
                <Sk className="h-4" style={{ width: `${w}px` }} />
              </div>
              <div className="col-span-2 flex justify-end"><Sk className="h-4 w-16" /></div>
              <div className="col-span-2 flex justify-end"><Sk className="h-4 w-20" /></div>
              <div className="col-span-2 flex justify-end"><Sk className="h-4 w-16" /></div>
              <div className="col-span-2 flex justify-end"><Sk className="h-4 w-12" /></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
)

const PortfolioPage: React.FC = () => {
  const {
    portfolio,
    loading,
    selectedPortfolioId,
    customPortfolios
  } = usePortfolioStore()

  if (!loading && portfolio.length === 0) {
    return (
      <motion.div variants={pageContainerVariants} initial="hidden" animate="visible">
        <motion.div variants={pageItemVariants}>
          <EmptyState />
        </motion.div>
      </motion.div>
    )
  }

  // Check if current portfolio is a Gold portfolio
  const selectedPortfolio = selectedPortfolioId ? customPortfolios.find(p => p.id === selectedPortfolioId) : null
  const isGoldPortfolio = selectedPortfolio?.name === 'ทองคำ' || selectedPortfolio?.port_type === 'port_gold'

  return (
    <motion.div variants={pageContainerVariants} initial="hidden" animate="visible" className="space-y-8">
      {isGoldPortfolio && selectedPortfolioId ? (
        /* Gold Portfolio View */
        <motion.div variants={pageItemVariants}>
          <GoldPortfolioSummary portfolioId={selectedPortfolioId} />
        </motion.div>
      ) : (
        /* Regular Portfolio View */
        <>
          {/* Market Indices Section */}
          <motion.div variants={pageItemVariants}>
            <MarketIndices />
          </motion.div>

          {/* Portfolio Chart with integrated summary */}
          <motion.div variants={pageItemVariants}>
            <PortfolioChart />
          </motion.div>
        </>
      )}
    </motion.div>
  )
}

export default PortfolioPage